import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2 } from "lucide-react"; // スピナー用
import { isEnglishOperatorMode, isMainSalesBreakerHost, rememberOperatorSurfaceFromLocation, withOperatorSurface } from "../utils/operatorMode";

// 🚀 修正点1: API_URL を動的に（現在のドメインまたはローカルホスト）
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

function ForgotPasswordPage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isOemDomain = !isMainSalesBreakerHost(window.location.hostname);
  const fallbackBrandName = isOemDomain ? "Partner Portal" : "Sales Breaker";
  const isEnglishBeta = isEnglishOperatorMode() && !isOemDomain;
  
  // 🚀 修正点2: ブランディング情報の保持用ステートを追加
  const [oemBranding, setOemBranding] = useState(null);

  // 🚀 修正点3: ページ読み込み時にブランド情報を取得
  useEffect(() => {
    rememberOperatorSurfaceFromLocation(location.search);
    const fetchBranding = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/oem/branding-by-domain?hostname=${window.location.hostname}`);
        const data = await res.json();
        
        if (data.success && data.is_oem) {
          setOemBranding(data.branding);
          // ブラウザタブのタイトルを更新
          document.title = `${data.branding.display_name} | パスワード再設定`;

          // ファビコンの動的書き換え
          if (data.branding.logo_path) {
            const iconUrl = `${window.location.origin}/api/oem${data.branding.logo_path}`;
            const existingIcons = document.querySelectorAll("link[rel*='icon']");
            existingIcons.forEach(el => el.parentNode.removeChild(el));
            const link = document.createElement('link');
            link.type = 'image/png';
            link.rel = 'shortcut icon';
            link.href = iconUrl;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
        }
      } catch (e) {
        console.error("Branding fetch error:", e);
      }
    };
    fetchBranding();
    if (isEnglishBeta) {
      document.title = "SalesBreaker | Password Recovery";
    }
  }, [isEnglishBeta, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          surface: isEnglishBeta ? 'english_beta' : undefined,
          operator_surface: isEnglishBeta ? 'english_beta' : undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || (isEnglishBeta ? 'Password recovery request failed.' : 'リクエストに失敗しました。'));
      }
      
      setMessage(isEnglishBeta
        ? 'If the email is registered, password reset instructions have been sent. Please check your inbox and spam folder.'
        : 'パスワード再設定のご案内をメールで送信しました。メールボックスをご確認ください。（メールが届かない場合でも、数分待ってから再度お試しください）');

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-3 sm:p-4 font-sans">
      <div className="w-full max-w-md p-5 sm:p-10 bg-white shadow-xl rounded-2xl sm:rounded-3xl border border-slate-100 animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center">
          <Link to="/" className="inline-block mb-6 h-16">
            {/* 🚀 修正点4: OEMロゴを表示（なければデフォルト） */}
            <img 
              src={oemBranding?.logo_path ? `${window.location.origin}/api/oem${oemBranding.logo_path}` : "/images/logo.png"} 
              alt="Logo" 
              className="max-h-full w-auto mx-auto" 
              onError={(e) => { 
                e.target.onerror = null; 
                e.target.src=isOemDomain ? 'https://placehold.co/128x128/6366f1/ffffff?text=Logo' : 'https://placehold.co/128x128/6366f1/ffffff?text=SB'; 
              }}
            />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 break-words">
            {oemBranding?.display_name || fallbackBrandName}
          </h1>
          <p className="mt-4 text-slate-500 font-bold text-sm uppercase tracking-widest">Password Recovery</p>
          <p className="mt-2 text-gray-400 text-xs">
            {isEnglishBeta ? "Enter the email address for your operator account." : "ご登録のメールアドレスを入力してください。"}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email-address" className="sr-only">{isEnglishBeta ? "Email address" : "メールアドレス"}</label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full px-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold text-sm"
              placeholder={isEnglishBeta ? "Email address" : "メールアドレス"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {message && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
              <p className="text-xs text-green-700 font-bold leading-relaxed">{message}</p>
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
              <p className="text-xs text-red-600 font-bold">{error}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 sm:py-5 text-white rounded-2xl font-black text-base sm:text-lg transition-all shadow-xl active:scale-[0.98] disabled:opacity-50 flex items-center justify-center"
              style={{ backgroundColor: oemBranding?.brand_color || '#4F46E5' }}
            >
              {isLoading ? <Loader2 className="animate-spin" size={24} /> : (isEnglishBeta ? 'Send reset instructions' : '再設定メールを送信')}
            </button>
          </div>
          
          <div className="text-center mt-6">
            <Link 
              to={withOperatorSurface("/login")} 
              className="text-xs font-black uppercase tracking-widest hover:opacity-70 transition-opacity"
              style={{ color: oemBranding?.brand_color || '#4F46E5' }}
            >
              {isEnglishBeta ? "Back to login" : "← ログインページに戻る"}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
